import { useMemo, useState } from 'react'
import { MenuTrigger } from 'react-aria-components'
import { ACCOUNT_PERMISSION } from '../../shared/constants'
import { ObjectProperties, SavedView } from '../../shared/types'
import { useHasAccountPermission } from '../account-permissions'
import { useSavedViewListAll } from '../api/saved-views'
import { Button } from '../components/button'
import { Menu, MenuItem, Popover } from '../components/menu'
import { RemoveViewModal, UpdateViewModal } from '../components/saved-view-modals'
import { quickDeepEqual } from '../query-deep-equal'

export function Views<T extends ObjectProperties = ObjectProperties>({
	onSetView,
	viewId,
	filter,
	object,
}: {
	onSetView: (viewId?: string, filter?: T) => void
	viewId?: string
	filter?: T
	object: string
}) {
	const { data: savedViews = [] } = useSavedViewListAll(object)
	const view = useMemo(() => {
		return savedViews?.find(savedView => quickDeepEqual(savedView.filter, filter))
	}, [savedViews, filter])

	const setView = (viewId?: string) => {
		if (!viewId) {
			return onSetView()
		}
		const view = savedViews?.find(savedView => savedView.id === viewId)
		if (view) {
			onSetView(viewId, view.filter)
		}
	}

	const [viewModal, setViewModal] = useState<[SavedView, 'rename' | 'delete'] | null>(null)

	const canEditViews = useHasAccountPermission(ACCOUNT_PERMISSION.EDIT_DASHBOARD_VIEWS)
	const canRemoveViews = useHasAccountPermission(ACCOUNT_PERMISSION.DELETE_DASHBOARD_VIEWS)

	return (
		<>
			<div className="flex flex-wrap items-center my-4 gap-1 px-5 sm:px-6 print:hidden">
				<span className="mr-2">Pohledy</span>
				{savedViews.map(savedView => (
					<div
						key={savedView.id}
						className={
							canEditViews || canRemoveViews ? 'rounded-md hover:bg-blue-700/10' : ''
						}
					>
						<Button
							onPress={() =>
								savedView.id === viewId ? setView() : setView(savedView.id)
							}
							variant="TEXT"
							size="SMALL"
							isActive={savedView.id === viewId}
							className={
								canEditViews || canRemoveViews ? `rounded-l-md !px-2` : 'rounded-md'
							}
						>
							{savedView.name}
						</Button>
						{canEditViews || canRemoveViews ? (
							<MenuTrigger>
								<Button
									variant="TEXT"
									size="SMALL"
									isActive={savedView.id === view?.id}
									className="rounded-r-md !px-2 hover:bg-rose-700/30 pressed:bg-rose-700/30"
								>
									⋮
								</Button>
								<Popover>
									<Menu>
										{canEditViews ? (
											<MenuItem
												onAction={() => setViewModal([savedView, 'rename'])}
											>
												Přejmenovat
											</MenuItem>
										) : null}
										{canRemoveViews ? (
											<MenuItem
												onAction={() => setViewModal([savedView, 'delete'])}
											>
												Smazat
											</MenuItem>
										) : null}
									</Menu>
								</Popover>
							</MenuTrigger>
						) : null}
					</div>
				))}
			</div>

			{viewModal?.[1] === 'rename' && (
				<UpdateViewModal
					object={object}
					view={viewModal[0]}
					onClose={() => setViewModal(null)}
				/>
			)}
			{viewModal?.[1] === 'delete' && (
				<RemoveViewModal
					object={object}
					view={viewModal[0]}
					onClose={() => setViewModal(null)}
				/>
			)}
		</>
	)
}
